import { CookieConsentService } from "@/services/shared/cookieConsent/CookieConsentPlugin"
import DatadogCapacitorPlugin from "@/services/shared/DatadogCapacitorPlugin"
import { datadogLogs } from "@datadog/browser-logs"
import { datadogRum, RumEventDomainContext } from "@datadog/browser-rum"
import { App, ComponentPublicInstance } from "vue"

import {
  canInitializeDatadog,
  clearHostOnlySessionCookie,
  commonDatadogParams,
  isServerError,
  sanitizeTagName,
  setRequestMetadata,
} from "./datadogHelper.ts"

interface DatadogPluginOptions {
  envResolver: () => string
  version: string
}

export default {
  install(app: App, options: DatadogPluginOptions) {
    if (canInitializeDatadog()) {
      const isConsentGiven = CookieConsentService.hasConsent("statistics")
      const trackingConsent = isConsentGiven ? "granted" : "not-granted"
      const env = sanitizeTagName(options.envResolver())

      clearHostOnlySessionCookie()

      datadogRum.init({
        ...commonDatadogParams,
        version: options.version,
        trackingConsent,
        env,
        applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
        allowedTracingUrls: [
          {
            match: /https:\/\/.*\.staging.soenergy\.co\/.*/,
            propagatorTypes: ["tracecontext"],
          },
          {
            match: /https:\/\/.*\.preview\.soenergy\.co\/.*/,
            propagatorTypes: ["tracecontext"],
          },
          {
            match: /https:\/\/.*\.so\.energy\/.*/,
            propagatorTypes: ["tracecontext"],
          },
        ],
        beforeSend: (event, context: RumEventDomainContext) => {
          const error = "error" in context ? context?.error : null
          if (isServerError(error)) {
            setRequestMetadata(event, error)
          }
          return true
        },
      })

      datadogLogs.init({
        ...commonDatadogParams,
        version: options.version,
        trackingConsent,
        env,
      })

      datadogRum.setGlobalContext({
        git: {
          commit: {
            sha: __APP_GIT_COMMIT_SHA__,
          },
          repository_url: "https://www.github.com/soenergy/fe-nexus",
        },
      })

      const originalErrorHandler = app.config.errorHandler
      app.config.errorHandler = function (
        error: unknown,
        vm: ComponentPublicInstance | null,
        info: string,
      ) {
        console.error(error) // Expose error in the console to facilitate debugging
        if (typeof originalErrorHandler === "function") {
          originalErrorHandler.call(this, error, vm, info)
        }
        const typedError = error as Error

        datadogRum.addError(typedError, {
          error: { kind: typedError.name, stack: typedError.stack },
          context: { info },
        })
      }

      CookieConsentService.runOnceCookieCategoryAccepted("statistics", () => {
        DatadogCapacitorPlugin.updateTrackingConsent({ enabled: true })
      })
    }
  },
}
